import * as React from "react";
import { api, postJson } from "./api";

export interface AppUser {
  handle: string;
  displayName: string;
  avatarUrl: string | null;
  subscriptionTier: string | null;
}

export interface RoomSnapshot {
  id: string;
  alias: string;
  title: string;
  visibility: "public" | "unlisted" | "private";
  status: string;
  roomKind: "user" | "house";
  stationSlug: string | null;
  sessionState: "ready" | "live" | "paused" | string;
  hostHandle: string | null;
  playbackLeaderHandle: string | null;
  participants: Array<{
    handle: string;
    displayName: string;
    avatarUrl: string | null;
    role: string;
  }>;
  queue: Array<any>;
  chat: Array<any>;
  nowPlaying: null | {
    spotifyId: string | null;
    uri: string;
    title: string;
    artist: string;
    album: string;
    coverUrl: string | null;
    durationMs: number | null;
    progressMs: number;
    isPlaying: boolean;
    updatedAt: string;
  };
}

interface RoomSessionValue {
  activeRoomRef: string | null;
  room: RoomSnapshot | null;
  connection: "idle" | "connecting" | "live" | "reconnecting" | "error";
  error: string;
  browserPlayer: { ready: boolean; deviceId: string; error: string };
  joinRoom: (roomRef: string, options?: { skipConfirmation?: boolean }) => Promise<boolean>;
  leaveRoom: () => void;
  refresh: () => Promise<void>;
  roomAction: (path: string, body?: unknown) => Promise<void>;
  activateBrowserPlayer: () => Promise<void>;
  listenOnDevice: (deviceId: string) => Promise<void>;
  playFragment: (fragmentId: string) => Promise<boolean>;
}

const RoomSessionContext = React.createContext<RoomSessionValue | null>(null);

export function RoomSessionProvider({ user, children }: { user: AppUser | null; children: React.ReactNode }) {
  const [activeRoomRef, setActiveRoomRef] = React.useState<string | null>(null);
  const [room, setRoom] = React.useState<RoomSnapshot | null>(null);
  const [connection, setConnection] = React.useState<RoomSessionValue["connection"]>("idle");
  const [error, setError] = React.useState("");
  const [browserPlayer, setBrowserPlayer] = React.useState({ ready: false, deviceId: "", error: "" });
  const socketRef = React.useRef<WebSocket | null>(null);
  const reconnectTimerRef = React.useRef<number | null>(null);
  const heartbeatRef = React.useRef<number | null>(null);
  const generationRef = React.useRef(0);
  const manuallyClosedRef = React.useRef(false);
  const sessionStateRef = React.useRef<{ activeRoomRef: string | null; room: RoomSnapshot | null; user: AppUser | null }>({
    activeRoomRef: null,
    room: null,
    user: null,
  });
  const lastPlaybackReportRef = React.useRef({ signature: "", at: 0 });
  const lastSpotifyStateRef = React.useRef<{
    uri: string | null;
    positionMs: number;
    durationMs: number;
    isPlaying: boolean;
    observedAt: number;
  } | null>(null);
  const spotifyPlayerRef = React.useRef<any>(null);
  const listeningRef = React.useRef<{ roomRef: string; deviceId: string } | null>(null);
  const playbackSyncSignatureRef = React.useRef("");

  React.useEffect(() => {
    sessionStateRef.current = { activeRoomRef, room, user };
  }, [activeRoomRef, room, user]);

  const closeSocket = React.useCallback(() => {
    generationRef.current += 1;
    if (heartbeatRef.current) window.clearInterval(heartbeatRef.current);
    if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
    heartbeatRef.current = null;
    reconnectTimerRef.current = null;
    const socket = socketRef.current;
    socketRef.current = null;
    if (socket && socket.readyState < WebSocket.CLOSING) socket.close(1000, "Session changed");
  }, []);

  const refresh = React.useCallback(async () => {
    if (!activeRoomRef) return;
    const payload = await api<{ room: RoomSnapshot }>(`/api/rooms/${encodeURIComponent(activeRoomRef)}`);
    setRoom(payload.room);
  }, [activeRoomRef]);

  const connectSocket = React.useCallback((roomRef: string, reconnecting = false) => {
    closeSocket();
    const generation = generationRef.current;
    manuallyClosedRef.current = false;
    setConnection(reconnecting ? "reconnecting" : "connecting");
    setError("");
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(`${protocol}//${window.location.host}/ws/rooms/${encodeURIComponent(roomRef)}`);
    socketRef.current = socket;
    socket.onopen = () => {
      if (generation !== generationRef.current) return;
      setConnection("live");
      heartbeatRef.current = window.setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({ type: "presence.heartbeat" }));
        }
      }, 20_000);
    };
    socket.onmessage = (event) => {
      if (generation !== generationRef.current) return;
      try {
        const message = JSON.parse(event.data);
        if (message.type === "room.snapshot") setRoom(message.data);
        if (message.type === "error.toast") setError(message.message || "Room update failed");
      } catch {
        setError("A room update could not be read");
      }
    };
    socket.onerror = () => {
      if (generation === generationRef.current) setError("Live room connection was interrupted");
    };
    socket.onclose = (event) => {
      if (generation !== generationRef.current || manuallyClosedRef.current) return;
      if (heartbeatRef.current) window.clearInterval(heartbeatRef.current);
      if (event.code === 1000) return;
      setConnection("reconnecting");
      reconnectTimerRef.current = window.setTimeout(() => connectSocket(roomRef, true), 1500);
    };
  }, [closeSocket]);

  const joinRoom = React.useCallback(async (roomRef: string, options?: { skipConfirmation?: boolean }) => {
    if (activeRoomRef && activeRoomRef !== roomRef && !options?.skipConfirmation) {
      const accepted = window.confirm("Leave the room you are synced to and join this one?");
      if (!accepted) return false;
    }
    if (activeRoomRef === roomRef && room) return true;
    listeningRef.current = null;
    playbackSyncSignatureRef.current = "";
    setActiveRoomRef(roomRef);
    setConnection("connecting");
    setError("");
    try {
      if (user) {
        await postJson(`/api/rooms/${encodeURIComponent(roomRef)}/join`, {});
      }
      const payload = await api<{ room: RoomSnapshot }>(`/api/rooms/${encodeURIComponent(roomRef)}`);
      setRoom(payload.room);
      connectSocket(roomRef);
      return true;
    } catch (cause: any) {
      setConnection("error");
      setError(cause?.message || "Could not join this room");
      return false;
    }
  }, [activeRoomRef, connectSocket, room, user]);

  const leaveRoom = React.useCallback(() => {
    manuallyClosedRef.current = true;
    closeSocket();
    setActiveRoomRef(null);
    setRoom(null);
    setConnection("idle");
    setError("");
    listeningRef.current = null;
    playbackSyncSignatureRef.current = "";
  }, [closeSocket]);

  React.useEffect(() => {
    if (!activeRoomRef) return;
    void joinRoom(activeRoomRef, { skipConfirmation: true });
    return () => {
      // The provider, not individual pages, owns the connection.
    };
  }, [user?.handle]);

  React.useEffect(() => () => {
    manuallyClosedRef.current = true;
    closeSocket();
  }, [closeSocket]);

  React.useEffect(() => {
    if (!user) {
      setBrowserPlayer({ ready: false, deviceId: "", error: "" });
      return;
    }
    let cancelled = false;
    let player: any = null;

    const ensureSdk = async () => {
      const spotifyWindow = window as any;
      if (spotifyWindow.Spotify?.Player) return;
      const existing = document.getElementById("spotify-player-sdk") as HTMLScriptElement | null;
      if (!existing) {
        const script = document.createElement("script");
        script.id = "spotify-player-sdk";
        script.src = "https://sdk.scdn.co/spotify-player.js";
        script.async = true;
        script.addEventListener("error", () => setBrowserPlayer((state) => ({ ...state, error: "Spotify’s browser player could not load." })));
        document.body.appendChild(script);
      }
      await new Promise<void>((resolve, reject) => {
        const startedAt = Date.now();
        const previousReady = spotifyWindow.onSpotifyWebPlaybackSDKReady;
        spotifyWindow.onSpotifyWebPlaybackSDKReady = () => {
          previousReady?.();
          resolve();
        };
        const timer = window.setInterval(() => {
          if (spotifyWindow.Spotify?.Player) {
            window.clearInterval(timer);
            resolve();
          } else if (Date.now() - startedAt > 15_000) {
            window.clearInterval(timer);
            reject(new Error("Spotify’s browser player timed out."));
          }
        }, 100);
      });
    };

    let playerDeviceId = "";
    const reportPlayback = async (state: any) => {
      const current = sessionStateRef.current;
      if (!current.activeRoomRef || current.room?.hostHandle !== current.user?.handle) return;
      if (!state) {
        try {
          await postJson(`/api/rooms/${encodeURIComponent(current.activeRoomRef)}/playback/pull`, {
            deviceId: playerDeviceId || undefined,
          });
        } catch { /* the host polling loop will retry */ }
        return;
      }
      const track = state.track_window?.current_track;
      const uri = typeof track?.uri === "string" ? track.uri : null;
      const now = Date.now();
      const durationMs = Math.max(0, Number(track?.duration_ms || 0));
      let progressMs = Math.max(0, Number(state.position || 0));
      const isPlaying = !state.paused;
      const previous = lastSpotifyStateRef.current;
      if (!isPlaying && progressMs === 0 && durationMs > 0 && previous?.isPlaying
        && previous.uri === uri
        && previous.positionMs + Math.max(0, now - previous.observedAt) >= durationMs - 2_000) {
        progressMs = durationMs;
      }
      lastSpotifyStateRef.current = { uri, positionMs: progressMs, durationMs, isPlaying, observedAt: now };
      const signature = `${uri || "none"}:${isPlaying ? 1 : 0}:${Math.floor(progressMs / 15_000)}`;
      if (signature === lastPlaybackReportRef.current.signature && now - lastPlaybackReportRef.current.at < 12_000) return;
      lastPlaybackReportRef.current = { signature, at: now };
      try {
        await postJson(`/api/rooms/${encodeURIComponent(current.activeRoomRef)}/playback/report`, {
          trackId: track?.id || null,
          uri,
          title: track?.name || null,
          artist: Array.isArray(track?.artists) ? track.artists.map((artist: any) => artist?.name).filter(Boolean).join(", ") : null,
          album: track?.album?.name || null,
          artworkUrl: track?.album?.images?.[0]?.url || null,
          durationMs,
          progressMs,
          isPlaying,
          deviceId: playerDeviceId || undefined,
        });
      } catch {
        lastPlaybackReportRef.current.signature = "";
      }
    };

    const setup = async () => {
      try {
        await ensureSdk();
        if (cancelled) return;
        const spotifyWindow = window as any;
        player = new spotifyWindow.Spotify.Player({
          name: "Jamband Browser Player",
          volume: 0.8,
          getOAuthToken: async (callback: (token: string) => void) => {
            try {
              const payload = await api<{ accessToken: string }>("/api/spotify/web-playback-token");
              callback(payload.accessToken);
            } catch (cause: any) {
              setBrowserPlayer((state) => ({ ...state, error: cause?.message || "Reconnect Spotify for browser playback." }));
            }
          },
        });
        spotifyPlayerRef.current = player;
        player.addListener("ready", ({ device_id: deviceId }: { device_id: string }) => {
          playerDeviceId = deviceId;
          if (!cancelled) setBrowserPlayer({ ready: true, deviceId, error: "" });
        });
        player.addListener("not_ready", () => {
          if (!cancelled) setBrowserPlayer((state) => ({ ...state, ready: false }));
        });
        for (const event of ["initialization_error", "authentication_error", "account_error", "playback_error"]) {
          player.addListener(event, ({ message }: { message: string }) => {
            if (!cancelled) setBrowserPlayer((state) => ({
              ...state,
              error: message === "Restriction violated"
                ? "Spotify restricted this playback command. Choose This browser again after reloading Jamband."
                : message || "Spotify browser playback is unavailable.",
            }));
          });
        }
        player.addListener("autoplay_failed", () => {
          if (!cancelled) setBrowserPlayer((state) => ({
            ...state,
            error: "Your browser blocked autoplay. Click Play or choose This browser again.",
          }));
        });
        player.addListener("player_state_changed", (state: any) => void reportPlayback(state));
        if (!await player.connect()) throw new Error("Spotify’s browser player could not connect.");
      } catch (cause: any) {
        if (!cancelled) setBrowserPlayer({ ready: false, deviceId: "", error: cause?.message || "Spotify browser playback is unavailable." });
      }
    };
    void setup();
    return () => {
      cancelled = true;
      spotifyPlayerRef.current = null;
      try { player?.disconnect?.(); } catch { /* player already closed */ }
    };
  }, [user?.handle]);

  React.useEffect(() => {
    if (!activeRoomRef || !user || room?.hostHandle !== user.handle) return;
    let inFlight = false;
    const pullPlayback = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        await postJson(`/api/rooms/${encodeURIComponent(activeRoomRef)}/playback/pull`, {
          deviceId: browserPlayer.deviceId || undefined,
        });
      } catch {
        // The next poll or SDK state change retries without interrupting the room UI.
      } finally {
        inFlight = false;
      }
    };
    void pullPlayback();
    const timer = window.setInterval(() => void pullPlayback(), 2_000);
    return () => window.clearInterval(timer);
  }, [activeRoomRef, browserPlayer.deviceId, room?.hostHandle, user?.handle]);

  const roomAction = React.useCallback(async (path: string, body: unknown = {}) => {
    if (!activeRoomRef) throw new Error("No active room");
    await postJson(`/api/rooms/${encodeURIComponent(activeRoomRef)}/${path}`, body);
  }, [activeRoomRef]);

  const activateBrowserPlayer = React.useCallback(async () => {
    try {
      await spotifyPlayerRef.current?.activateElement?.();
    } catch {
      // Spotify may already have an activated audio element.
    }
  }, []);

  const listenOnDevice = React.useCallback(async (deviceId: string) => {
    const current = sessionStateRef.current;
    if (!current.activeRoomRef || !current.room || !current.user) throw new Error("Connect Spotify to listen");
    if (!deviceId) throw new Error("Choose a Spotify device");
    if (deviceId === browserPlayer.deviceId) await activateBrowserPlayer();
    if (current.room.roomKind === "house" && current.room.stationSlug) {
      await postJson(`/api/house-stations/${encodeURIComponent(current.room.stationSlug)}/listen`, { deviceId });
    } else {
      await postJson(`/api/rooms/${encodeURIComponent(current.activeRoomRef)}/playback/play-on-device`, { deviceId });
    }
    listeningRef.current = { roomRef: current.activeRoomRef, deviceId };
    playbackSyncSignatureRef.current = `${current.room.nowPlaying?.uri || "none"}:${current.room.nowPlaying?.isPlaying ? 1 : 0}`;
  }, [activateBrowserPlayer, browserPlayer.deviceId]);

  React.useEffect(() => {
    const listening = listeningRef.current;
    if (!listening || listening.roomRef !== activeRoomRef || !room || !user) return;
    const signature = `${room.nowPlaying?.uri || "none"}:${room.nowPlaying?.isPlaying ? 1 : 0}`;
    if (signature === playbackSyncSignatureRef.current) return;
    playbackSyncSignatureRef.current = signature;
    if (room.hostHandle === user.handle) return;
    void postJson(`/api/rooms/${encodeURIComponent(activeRoomRef)}/playback/sync-on-device`, {
      deviceId: listening.deviceId,
    }).catch((cause: any) => {
      playbackSyncSignatureRef.current = "";
      setError(cause?.message || "Your Spotify device fell out of station sync");
    });
  }, [activeRoomRef, room?.hostHandle, room?.nowPlaying?.isPlaying, room?.nowPlaying?.uri, user?.handle]);

  const playFragment = React.useCallback(async (fragmentId: string) => {
    if (activeRoomRef && !window.confirm("Playing this song personally will take you out of room sync. Continue?")) {
      return false;
    }
    await activateBrowserPlayer();
    await postJson(`/api/fragments/${encodeURIComponent(fragmentId)}/play`, {
      deviceId: browserPlayer.deviceId || undefined,
    });
    listeningRef.current = null;
    playbackSyncSignatureRef.current = "";
    return true;
  }, [activateBrowserPlayer, activeRoomRef, browserPlayer.deviceId]);

  return (
    <RoomSessionContext.Provider value={{
      activeRoomRef,
      room,
      connection,
      error,
      browserPlayer,
      joinRoom,
      leaveRoom,
      refresh,
      roomAction,
      activateBrowserPlayer,
      listenOnDevice,
      playFragment,
    }}>
      {children}
    </RoomSessionContext.Provider>
  );
}

export function useRoomSession() {
  const context = React.useContext(RoomSessionContext);
  if (!context) throw new Error("useRoomSession must be used within RoomSessionProvider");
  return context;
}
